// Purchasing Request Detail — Phase 1E-δ Batch 1
// Detail + item editor (DRAFT saja) + submit/approve/reject/cancel.
//
// Guard "requester tidak boleh approve": tombol Setujui/Tolak DISEMBUNYIKAN
// dan diganti banner penjelas — pola sama dengan pages/inventory/Adjustments.
// Ini kosmetik; otoritasnya tetap backend (pr_service REQUESTER_CANNOT_APPROVE),
// berlaku untuk approve DAN reject, dan Owner pun tidak bypass.
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { StatusBadge, PURCHASE_REQUEST_STATUS_TONE } from "../../components/etams/StatusBadge";
import { BusyButton } from "../../components/etams/BusyButton";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { ArrowLeft, RefreshCw, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDateTime, formatQty } from "../../lib/format";
import { useStoreTimezones } from "../../lib/timezone";
import {
  getPR, patchPRItems, submitPR, approvePR, rejectPR, cancelPR,
  listPOs, apiErrorMessage,
} from "../../lib/purchasingApi";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariBahan, labelBahan } from "../../lib/sumberPilihan";
import { bolehPutusPR } from "../../lib/aksiPurchasing";

// `material` = objek bahan (dari PemilihCari, atau `it.material` kiriman backend).
const EMPTY_ITEM = { material_id: "", material: null, qty_requested: "", estimated_unit_price: "", notes: "" };
// Kode satuan → kata yang tampil.
const LABEL_UOM = { METER: "Meter", KG: "Kg", LITER: "Liter", PCS: "Pcs", ROLL: "Roll", PACK: "Pack", OTHER: "Lainnya" };
const satuan = (u) => (u ? LABEL_UOM[u] || u : "");

export default function PurchaseRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { roleCode, profile } = useAuth();
  const { tzForRegion } = useStoreTimezones();

  // Redesign 2026-07-29: hanya Kepala Cabang + Admin Gudang yang boleh
  // membuat/mengedit/mengajukan PR. Owner sengaja dikecualikan.
  const canWrite = ["KEPALA_CABANG", "ADMIN_GUDANG"].includes(roleCode);
  const canApprove = ["OWNER", "KEPALA_CABANG"].includes(roleCode);
  const canCancel = ["OWNER", "KEPALA_CABANG", "ADMIN_PURCHASING"].includes(roleCode);

  const [pr, setPr] = useState(null);
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [regions, setRegions] = useState([]);

  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getPR(id);
      setPr(r.data);
      // Tautan balik ke PO: 1 PR = 1 PO, lahir otomatis saat PR disetujui.
      // Kegagalannya tidak boleh menjatuhkan halaman PR.
      listPOs({ pr_id: id, page: 1, page_size: 1 })
        .then(res => setPo((res.data.items || [])[0] || null))
        .catch((e) => { setPo(null); toast.error(apiErrorMessage(e, "Data gagal dimuat.")); });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat PR."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Bahan TIDAK lagi dimuat seluruhnya (28 Sep 2026): nama bahan tiap baris ikut di
    // rincian PR (`it.material`), dan kotak pilih bahan mencari di server.
    api.get("/regions").then(r => setRegions(r.data || [])).catch((e) => { setRegions([]); toast.error(apiErrorMessage(e, "Daftar wilayah gagal dimuat — muat ulang halaman.")); });
  }, []);

  const materialLabel = (mid) => {
    const m = (pr?.items || []).find(it => it.material_id === mid)?.material;
    return m ? `${m.code} — ${m.name}` : "—";
  };
  const regionName = (rid) => regions.find(r => r.id === rid)?.name || "—";

  const startEdit = () => {
    setRows((pr.items || []).map(it => ({
      material_id: it.material_id,
      material: it.material || { id: it.material_id, code: "", name: "Bahan tersimpan", unit_of_measure: it.unit_of_measure },
      qty_requested: String(it.qty_requested ?? ""),
      estimated_unit_price: it.estimated_unit_price == null ? "" : String(it.estimated_unit_price),
      notes: it.notes || "",
    })));
    setEditing(true);
  };

  const setRow = (i, patch) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows(rs => [...rs, { ...EMPTY_ITEM }]);
  const removeRow = (i) => setRows(rs => (rs.length === 1 ? rs : rs.filter((_, idx) => idx !== i)));

  const saveItems = async () => {
    const clean = rows.filter(r => r.material_id && Number(r.qty_requested) > 0);
    if (clean.length === 0) {
      toast.error("Minimal satu item dengan bahan dan jumlah lebih dari 0.");
      return;
    }
    setBusy(true);
    try {
      await patchPRItems(id, {
        items: clean.map(r => {
          const mat = r.material;
          return {
            material_id: r.material_id,
            qty_requested: Number(r.qty_requested),
            unit_of_measure: mat?.unit_of_measure || null,
            estimated_unit_price: r.estimated_unit_price === ""
              ? null : Number(r.estimated_unit_price),
            notes: r.notes || null,
          };
        }),
      });
      toast.success("Item PR diperbarui.");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyimpan item."));
    } finally {
      setBusy(false);
    }
  };

  const runAction = async (fn, okMsg, failMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      setRejectOpen(false);
      setCancelOpen(false);
      setReason("");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, failMsg));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="pr-detail-loading">
        <div className="h-8 w-56 bg-muted rounded animate-pulse" />
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-12 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (!pr) {
    return (
      <div className="p-12 text-center text-muted-foreground text-sm" data-testid="pr-detail-empty">
        PR tidak ditemukan — mungkin sudah dihapus, atau bukan bagian wilayah Anda.
        <div className="mt-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/purchasing/pr")}>
            Kembali ke daftar
          </Button>
        </div>
      </div>
    );
  }

  const b = PURCHASE_REQUEST_STATUS_TONE[pr.status] || { l: pr.status, tone: "neutral" };
  const isSelf = pr.requested_by === profile?.id;

  // Harga wajib saat submit (migration 025). Backend menolak dengan 400
  // PR_ITEM_PRICE_REQUIRED — ini hanya supaya orang tidak menekan tombol
  // yang sudah pasti gagal, dan tahu item MANA yang kurang.
  const itemsTanpaHarga = (pr.items || []).filter(it => it.estimated_unit_price == null);

  const canEditItems  = canWrite && pr.status === "DRAFT";
  const canSubmit     = canWrite && pr.status === "DRAFT"
                        && (pr.items || []).length > 0
                        && itemsTanpaHarga.length === 0;
  // U2-C9: routing penyetuju dari backend (`pr.penyetuju`) — PR Admin Gudang hanya diputus Kepala Cabang cabangnya.
  const canApproveThis = canApprove && bolehPutusPR(pr, roleCode, profile?.id);
  const bukanPenyetuju = canApprove && pr.status === "SUBMITTED" && !isSelf && !canApproveThis;
  const requesterCantApproveWarning = canApprove && pr.status === "SUBMITTED" && isSelf;
  const canCancelThis = canCancel && ["DRAFT", "SUBMITTED"].includes(pr.status);

  const displayRows = editing ? rows : (pr.items || []);
  const editTotal = rows.reduce((s, r) =>
    s + (Number(r.qty_requested) || 0) * (Number(r.estimated_unit_price) || 0), 0);

  return (
    <div className="space-y-6" data-testid="pr-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/purchasing/pr")}
                  data-testid="pr-detail-back" className="-ml-2 mb-1">
            <ArrowLeft className="h-4 w-4 mr-1" /> Daftar PR
          </Button>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            {pr.pr_number} <StatusBadge tone={b.tone}>{b.l}</StatusBadge>
          </h1>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="pr-detail-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
        </Button>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><span className="text-muted-foreground block text-xs">Wilayah</span>{regionName(pr.region_id)}</div>
          <div><span className="text-muted-foreground block text-xs">Dibuat</span>{formatDateTime(pr.requested_at, tzForRegion(pr.region_id))}</div>
          <div><span className="text-muted-foreground block text-xs">Diajukan</span>{formatDateTime(pr.submitted_at, tzForRegion(pr.region_id))}</div>
          <div>
            <span className="text-muted-foreground block text-xs">Estimasi Total</span>
            <span className="font-semibold">{formatIDR(pr.total_estimated_amount)}</span>
          </div>
        </div>
        {pr.notes && (
          <div className="mt-3 text-sm">
            <span className="text-muted-foreground text-xs block">Catatan</span>{pr.notes}
          </div>
        )}
        {pr.reject_reason && (
          <div className="mt-3 bg-danger-soft border border-danger/30 rounded p-2 text-sm text-danger-soft-foreground"
               data-testid="pr-reject-reason">
            <span className="font-medium">Alasan Tolak:</span> {pr.reject_reason}
          </div>
        )}
        {pr.cancel_reason && (
          <div className="mt-3 bg-muted border rounded p-2 text-sm"
               data-testid="pr-cancel-reason">
            <span className="font-medium">Alasan Batal:</span> {pr.cancel_reason}
          </div>
        )}
      </Card>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/50">
          <span className="text-sm font-medium">Item ({displayRows.length})</span>
          <div className="flex gap-2">
            {canEditItems && !editing && (
              <Button variant="outline" size="sm" onClick={startEdit} data-testid="pr-edit-items">
                Ubah Item
              </Button>
            )}
            {editing && (
              <>
                <Button variant="outline" size="sm" onClick={addRow} data-testid="pr-detail-add-row">
                  <Plus className="h-4 w-4 mr-1" /> Baris
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={busy}>Batal</Button>
                <BusyButton size="sm" onClick={saveItems} busy={busy} busyText="Menyimpan..."
                            data-testid="pr-save-items">
                  <Save className="h-4 w-4 mr-1" /> Simpan
                </BusyButton>
              </>
            )}
          </div>
        </div>

        {editing ? (
          <div className="p-4 space-y-2" data-testid="pr-detail-editor">
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-start">
                <PemilihCari className="col-span-5" value={r.material}
                             onChange={(m) => setRow(i, { material: m || null, material_id: m?.id || "" })}
                             cari={cariBahan} label={labelBahan}
                             keterangan={(m) => m?.unit_of_measure || ""}
                             placeholder="— pilih bahan —" placeholderCari="Ketik kode atau nama bahan…"
                             testId={`pr-detail-material-${i}`} />
                <Input className="col-span-2" type="number" min="0" step="any" placeholder="Jumlah"
                       value={r.qty_requested} data-testid={`pr-detail-qty-${i}`}
                       onChange={(e) => setRow(i, { qty_requested: e.target.value })} />
                <Input className={`col-span-3 ${r.material_id && r.estimated_unit_price === ""
                         ? "border-warning bg-warning-soft" : ""}`}
                       type="number" min="0" placeholder="Harga satuan *"
                       value={r.estimated_unit_price} data-testid={`pr-detail-price-${i}`}
                       onChange={(e) => setRow(i, { estimated_unit_price: e.target.value })} />
                <div className="col-span-1 text-xs text-muted-foreground pt-2">
                  {satuan(r.material?.unit_of_measure)}
                </div>
                <Button variant="ghost" size="sm" className="col-span-1"
                        onClick={() => removeRow(i)} disabled={rows.length === 1}
                        data-testid={`pr-detail-remove-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <p className="text-xs text-muted-foreground pt-1">
              Harga satuan boleh dikosongkan selama PR masih draf, tapi wajib
              lengkap sebelum diajukan. Harga <strong>0</strong> sah (barang
              gratis/hibah) dan berbeda dari kosong.
            </p>
            <div className="text-right text-sm pt-2">
              Estimasi total: <span className="font-semibold">{formatIDR(editTotal)}</span>
            </div>
          </div>
        ) : displayRows.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm space-y-3">
            <p>PR ini belum punya item — PR tidak bisa diajukan tanpa item.</p>
            {canEditItems ? (
              <Button variant="outline" size="sm" onClick={startEdit} data-testid="pr-empty-edit-items">
                Ubah Item
              </Button>
            ) : (
              <p>Hanya pembuat PR (Kepala Cabang / Admin Gudang) yang dapat menambah item, selagi masih draf.</p>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">Bahan</th>
                <th className="px-4 py-3 font-medium text-right">Jumlah</th>
                <th className="px-4 py-3 font-medium">Satuan</th>
                <th className="px-4 py-3 font-medium text-right">Harga Satuan</th>
                <th className="px-4 py-3 font-medium text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((it) => (
                <tr key={it.id} className="border-b" data-testid={`pr-item-${it.id}`}>
                  <td className="px-4 py-3">{materialLabel(it.material_id)}</td>
                  <td className="px-4 py-3 text-right">{formatQty(it.qty_requested)}</td>
                  <td className="px-4 py-3">{satuan(it.unit_of_measure) || "—"}</td>
                  <td className="px-4 py-3 text-right">{formatIDR(it.estimated_unit_price)}</td>
                  <td className="px-4 py-3 text-right">{formatIDR(it.estimated_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {po && (
        <div className="text-sm bg-info-soft text-info-soft-foreground border border-info/30 rounded p-3 flex
                        items-center justify-between gap-3 flex-wrap"
             data-testid="pr-po-link">
          <span>
            PO untuk PR ini sudah lahir otomatis:{" "}
            <button type="button" data-testid="pr-po-link-btn"
                    onClick={() => navigate(`/purchasing/po/${po.id}`)}
                    className="font-medium underline underline-offset-2 hover:opacity-80">
              {po.po_number}
            </button>
            {po.vendor?.name
              ? <> · vendor {po.vendor.name}</>
              : <span> · vendor belum ditetapkan</span>}
          </span>
        </div>
      )}

      {canWrite && pr.status === "DRAFT" && itemsTanpaHarga.length > 0 && (
        <div className="text-sm bg-warning-soft text-warning-soft-foreground border border-warning/40 rounded p-3"
             data-testid="pr-price-required-warning">
          ⚠️ <strong>{itemsTanpaHarga.length} dari {(pr.items || []).length} item</strong> belum
          punya estimasi harga satuan, jadi PR ini belum bisa diajukan.
          <div className="mt-1">
            Penyetuju tidak bisa menilai PR tanpa tahu berapa uang yang keluar.
            Klik <em>Ubah Item</em> dan lengkapi kolom harga
            {itemsTanpaHarga.length <= 5 && (
              <> untuk: {itemsTanpaHarga.map(it => materialLabel(it.material_id)).join(", ")}</>
            )}.
          </div>
        </div>
      )}

      {requesterCantApproveWarning && (
        <div className="text-sm bg-warning-soft text-warning-soft-foreground border border-warning/40 rounded p-3"
             data-testid="pr-requester-warning">
          ⚠️ Anda pemohon PR ini; persetujuan harus dari Owner/Kepala Cabang lain.
          Aturan ini berlaku juga untuk penolakan, dan Owner pun tidak bisa melewatinya.
        </div>
      )}

      {bukanPenyetuju && (
        <div className="text-sm text-muted-foreground" data-testid="pr-bukan-penyetuju">
          PR ini disetujui atau ditolak oleh {(pr.penyetuju || []).includes("KEPALA_CABANG")
            ? "Kepala Cabang cabang PR ini" : "Owner"}.
        </div>
      )}

      {!editing && (
        <div className="flex gap-2 flex-wrap justify-end">
          {canSubmit && (
            <BusyButton onClick={() => runAction(() => submitPR(id), "PR diajukan.", "Gagal mengajukan PR.")}
                    busy={busy} className="bg-brand-orange text-brand-orange-foreground hover:bg-brand-orange/90"
                    data-testid="pr-submit-btn">
              Ajukan
            </BusyButton>
          )}
          {canApproveThis && (
            <>
              <BusyButton className="bg-brand-orange text-brand-orange-foreground hover:bg-brand-orange/90"
                      busy={busy} data-testid="pr-approve-btn"
                      onClick={() => runAction(() => approvePR(id), "PR disetujui.", "Gagal menyetujui PR.")}>
                Setujui
              </BusyButton>
              <Button variant="destructive" disabled={busy} data-testid="pr-reject-btn"
                      onClick={() => { setReason(""); setRejectOpen(true); }}>
                Tolak
              </Button>
            </>
          )}
          {canCancelThis && (
            <Button variant="outline" disabled={busy} data-testid="pr-cancel-btn"
                    onClick={() => { setReason(""); setCancelOpen(true); }}>
              Batalkan
            </Button>
          )}
        </div>
      )}

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="pr-reject-dialog">
          <DialogHeader><DialogTitle>Tolak PR {pr.pr_number}</DialogTitle></DialogHeader>
          <label className="block">
            <span className="text-sm">Alasan <span aria-hidden="true">*</span> (min. 3 karakter)</span>
            <Textarea rows={3} value={reason} data-testid="pr-reject-reason-input"
                      onChange={(e) => setReason(e.target.value)} />
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={busy}>Batal</Button>
            <BusyButton variant="destructive" busy={busy} disabled={reason.trim().length < 3}
                    busyText="Menolak..." data-testid="pr-reject-confirm"
                    onClick={() => runAction(() => rejectPR(id, { reason: reason.trim() }),
                                             "PR ditolak.", "Gagal menolak PR.")}>
              Tolak
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="pr-cancel-dialog">
          <DialogHeader><DialogTitle>Batalkan PR {pr.pr_number}</DialogTitle></DialogHeader>
          <label className="block">
            <span className="text-sm">Alasan <span aria-hidden="true">*</span> (min. 3 karakter)</span>
            <Textarea rows={3} value={reason} data-testid="pr-cancel-reason-input"
                      onChange={(e) => setReason(e.target.value)} />
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={busy}>Tutup</Button>
            <BusyButton variant="destructive" busy={busy} disabled={reason.trim().length < 3}
                    busyText="Membatalkan..." data-testid="pr-cancel-confirm"
                    onClick={() => runAction(() => cancelPR(id, { reason: reason.trim() }),
                                             "PR dibatalkan.", "Gagal membatalkan PR.")}>
              Batalkan PR
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
